<template>
  <div
    :class="[
      'd-flex',
      {
        'flex-column gap-2': orientation === 'column',
        'flex-row flex-wrap flex-gap-row': orientation === 'row',
        'checkbox-grid': orientation === 'grid',
      },
    ]"
  >
    <v-checkbox
      v-for="(value, label) in options"
      :key="`checkbox_option_${label}`"
      data-cy="checkbox"
      :label="label"
      class="custom-checkbox"
      :value="value"
      v-model="modelValue"
      :rules="rules"
      hide-details
      dense
    />
    <v-checkbox
      v-if="hasOther"
      class="custom-checkbox other"
      v-model="other.selected"
      :rules="rules"
      :value="true"
      hide-details
      data-cy="other_checkbox"
      dense
      @change="handleOtherCheckboxChange"
    >
      <template #label>
        <v-text-field
          data-cy="other_textfield"
          filled
          dense
          placeholder="Outro"
          v-model="other.text"
          :disabled="!other"
          hide-details="auto"
          @click.stop
          @input="handleOtherTextChange"
          :rules="other.selected ? [$validation.required()] : []"
        />
      </template>
    </v-checkbox>
  </div>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash'
import { PropOptions, WatchOptionsWithHandler } from 'vue'

/**
 * @typedef {(string|number)[]} ModelValue
 */

export default {
  name: 'CheckboxGroup',
  props: {
    /** @type {PropOptions<ModelValue>} */
    value: {
      type: Array,
    },
    /** @type {PropOptions<Record<string, string | number | boolean>>} */
    options: {
      type: Object,
      required: true,
    },
    /** @type {PropOptions<'row' | 'column' | 'grid'>} */
    orientation: {
      type: String,
      default: 'row',
    },
    hasOther: {
      type: Boolean,
      default: false,
    },
    /** @type {PropOptions<Array<(v: any) => boolean | string>>} */
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const modelValue = isEmpty(this.value) ? [] : cloneDeep(this.value)

    return {
      modelValue,
      other: {
        selected: false,
        /** @type {string?} */
        text: null,
      },
    }
  },
  methods: {
    handleOtherCheckboxChange(selected) {
      if (!selected) {
        return
      }
      this.other.text = ' '
      this.$nextTick(() => {
        this.other.text = ''
      })
    },
    handleOtherTextChange(text) {
      if (text?.length && !this.other.selected) {
        this.other.selected = true
      } else if (!text?.length && this.other.selected) {
        this.other.selected = false
      }
    },
  },
  watch: {
    /** @type {WatchOptionsWithHandler<ModelValue>} */
    modelValue: {
      handler(value) {
        const values = cloneDeep(value)
        if (this.other.selected && this.other.text?.trim().length) {
          values.push(this.other.text)
        }
        this.$emit('input', values)
      },
      deep: true,
    },
  },
}
</script>
<style scoped>
.custom-checkbox {
  margin: 0;
  padding: 0;
}

.custom-checkbox :deep(label) {
  font-size: 14px;
  color: var(--v-neutral-lighten17);
}

.custom-checkbox :deep(.v-input__slot) {
  font-size: 14px;
}

.custom-checkbox :deep(i) {
  font-size: 20px;
}
.custom-checkbox :deep(i.mdi-checkbox-blank-outline) {
  font-size: 20px;
}

.custom-checkbox :deep(.v-input--selection-controls__ripple) {
  font-size: 20px;
}

.custom-checkbox.other > :deep(.v-input__control > .v-input__slot) {
  align-items: start;
}

.custom-checkbox.other :deep(.v-input--selection-controls__input) {
  margin-top: 10px;
}

.flex-gap-row {
  row-gap: 8px;
  column-gap: 24px;
}

.checkbox-grid {
  display: grid !important;
  grid-template-columns: repeat(3, 1fr);
  align-items: start;
  row-gap: 4px;
  column-gap: 24px;
}
.checkbox-grid .custom-checkbox {
  min-height: 40px;
  max-height: 66px;
  align-items: center;
}
</style>
